import { DEV } from "solid-js"
import { unwrap } from "solid-js/store"
import type { Engine } from "../../core/engine/Engine.type"
import Unexpected from "../../Exception/Unexpected.class"

type SimpleAccessor<T> = (engine: Engine) => T
type SimpleMutator<T> = (engine: Engine, value: T) => void
type SimpleSignal<T> = [SimpleAccessor<T>, SimpleMutator<T>]

export default function getSetEngineWeakMapFactory<T>(
  makeDefaultFn?: (engine: Engine) => T
): SimpleSignal<T> {
  const weakMap = new WeakMap<Engine, T>()

  function getter (engine: Engine): T {
    if (DEV) {
      if (!engine) {
        throw new Unexpected('!engine')
      }
    }
    const unwrappedEngine = unwrap(engine)

    const value = weakMap.get(unwrappedEngine)!
    // nobody should be asking for controls before they are created
    if (!value && makeDefaultFn) {
      const defaultValue = makeDefaultFn(unwrappedEngine)
      weakMap.set(unwrappedEngine, defaultValue)
      return defaultValue
    }
    if (DEV) {
      if (!value) {
        throw new Unexpected('!value')
      }
    }
    return value
  }

  function setter (engine: Engine, value: T): void { 
    if (DEV) {
      if (!engine) {
        throw new Unexpected('!engine')
      }
    }
    const unwrappedEngine = unwrap(engine)
    weakMap.set(unwrappedEngine, value)
  }

  return [ getter, setter ]
}
